import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import ArticleContent from "../components/blog/ArticleContent";
import ArticleCard from "../components/blog/ArticleCard";
import BlogSlider from "../components/blog/slider";
import { getBlogEmpresarialBundle } from "../data/blogEmpresarialTranslations";
import { useEmpresasLanguage } from "../context/EmpresasLanguageContext";
import { resolveArticleCover } from "../utils/articleCoverImage";

const BASE_PATH = "/empresas/blog";

const BlogEmpresarialArticle = () => {
  const { category: categorySlug, slug } = useParams();
  const { lang } = useEmpresasLanguage();
  const { articles, categories, ui } = useMemo(
    () => getBlogEmpresarialBundle(lang),
    [lang]
  );
  const category = categories[categorySlug];

  const article = useMemo(
    () =>
      articles.find((item) => item.slug === slug && item.category === categorySlug),
    [articles, categorySlug, slug]
  );

  const relatedArticles = useMemo(
    () =>
      articles
        .filter((item) => item.category === categorySlug && item.slug !== slug)
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
        .slice(0, 3),
    [articles, categorySlug, slug]
  );

  if (!article || !category) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 bg-[#f7fafc] px-6 text-center">
        <h1 className="text-3xl font-bold text-[#123070]">{ui.articleNotFoundTitle}</h1>
        <p className="text-gray-600 max-w-md">{ui.articleNotFoundBody}</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link
            to={BASE_PATH}
            className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition-colors duration-150"
          >
            {ui.goToBlog}
          </Link>
          {categorySlug && (
            <Link
              to={`${BASE_PATH}/${categorySlug}`}
              className="inline-flex items-center gap-2 px-5 py-3 border border-blue-200 text-blue-700 font-semibold rounded-full hover:border-blue-400 transition-colors duration-150"
            >
              {ui.viewCategory}
            </Link>
          )}
        </div>
      </div>
    );
  }

  const cover = resolveArticleCover(article, categories);

  return (
    <>
      <header className="relative">
        <div
          className="h-[340px] sm:h-[420px] bg-cover bg-center"
          style={{ backgroundImage: `url(${cover.src})` }}
          role="img"
          aria-label={cover.alt}
        >
          <div className="h-full w-full bg-[#0f1a49]/80">
            <div className="max-w-4xl mx-auto h-full flex flex-col justify-center px-6 sm:px-10 lg:px-12 text-white">
              <nav className="flex flex-wrap items-center gap-2 text-sm text-white/70 mb-6">
                <Link to={BASE_PATH} className="hover:text-white font-medium">
                  {ui.blogEmpresarial}
                </Link>
                <span>/</span>
                <Link
                  to={`${BASE_PATH}/${category.slug}`}
                  className="hover:text-white font-medium"
                >
                  {category.title}
                </Link>
                <span>/</span>
                <span className="font-semibold text-white">{article.title}</span>
              </nav>
              <h1 className="text-4xl sm:text-5xl font-bold leading-tight">{article.title}</h1>
              <p className="mt-4 text-lg text-white/80 max-w-3xl">{article.excerpt}</p>
            </div>
          </div>
        </div>
      </header>
      <ArticleContent
        article={article}
        categories={categories}
        variant="empresas"
        byAuthorLabel={ui.byAuthor}
        keyTakeawaysTitle={ui.keyTakeawaysTitle}
      />
      {relatedArticles.length > 0 && (
        <section className="bg-[#f4f9fb] py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-10">
              <div>
                <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide">
                  {ui.alsoMayInterest}
                </p>
                <h2 className="text-3xl font-bold text-[#123070] mt-2">
                  {ui.moreArticlesIn} {category.title}
                </h2>
              </div>
              <Link
                to={`${BASE_PATH}/${category.slug}`}
                className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-900 transition-colors duration-150"
              >
                {ui.viewAll}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {relatedArticles.map((related) => (
                <ArticleCard
                  key={related.slug}
                  article={related}
                  basePath={BASE_PATH}
                  categories={categories}
                  readArticleLabel={ui.readFullArticle}
                />
              ))}
            </div>
          </div>
        </section>
      )}
      <BlogSlider
        items={relatedArticles}
        basePath={BASE_PATH}
        categories={categories}
        articles={articles}
        badgeLabel={ui.sliderBadge}
        titleLabel={ui.sliderTitle}
        readArticleLabel={ui.readArticle}
        prevAriaLabel={ui.prevArticle}
        nextAriaLabel={ui.nextArticle}
      />
    </>
  );
};

export default BlogEmpresarialArticle;
